<!-- 
  RecruitmentMediaInfoSetting.vue
  媒体情報設定モーダル
  媒体連携している媒体側の各種情報設定を行う

  coller
 -->
<template>
  <div>
    <!-- 処理中モーダル -->
    <LoadingModal
      v-if="isResponseWaiting"
      :dispTitle="loadingTitle"
      :isShowModal="isResponseWaiting"
      :dispMessage="loadingMessage"
    />
    <!-- アラートモーダル -->
    <AlertModal
      v-if="isAlertOpen"
      :isShowModal="isAlertOpen"
      :alertData="alertData"
      :alertResult="alertResult"
    />
    <!-- 会社選択モーダル -->
    <CompanySelectModal
      v-if="isCompanySelect"
      :isShowModal="isCompanySelect"
      :companyList="companyList"
      :selectedCompany="selectedCompany"
    />
    <!-- 媒体情報設定モーダル -->
    <div class="setting-component">
      <!-- 媒体選択操作 -->
      <div class="setting-header" ref="settingHeader">
        <!-- 既存設定ありの場合はプルダウンを表示 -->
        <div class="media-select-area">
          <!-- 設定名選択表示 -->
          <div class="name-disp">
            <!-- 媒体項目マスタープルダウン -->
            <select class="setting-select" v-model="selectedMediaIndex" @change="changeMedia()">
              <option
                v-for="(recruitmentMedia, recruitmentMediaIndex) in recruitmentMediaInfoList"
                :key="recruitmentMedia.media_name + '_' + recruitmentMediaIndex"
                :value="recruitmentMediaIndex"
              >
                {{ recruitmentMedia.media_name }}
              </option>
            </select>
            <!-- 有効/無効 -->
            <button class="toggle-apply button is-small" :class="{'is-primary': isAvailable === false}" @click="workingSwitch()">
              {{isAvailable ? '無効にする' : '有効にする'}}
            </button>
          </div>
        </div>
        <!-- 操作パネル (媒体名編集中は表示しない) -->
        <div class="operation-button-area">
          <!-- 編集操作 -->
          <div class="setting-edit-area">
            <!-- 未編集時の表示 -->
            <div v-if="isSettingEditable() === false">
              <!-- 媒体編集ボタン -->
              <button class="setting-edit button is-small" :class="{'is-primary': isSettingEditable() === false}" @click="editSetting()">
                媒体編集
              </button>
              <!-- 新規作成ボタン -->
              <button class="setting-create button is-small" @click="createMaster()">
                新規作成
              </button>
            </div>
            <!-- 編集中の表示 -->
            <div v-else>
              <!-- 設定保存ボタン -->
              <button class="setting-edit button is-small is-primary" @click="saveMaster()">
                保存
              </button>
              <!-- 編集キャンセルボタン -->
              <button class="setting-edit-cancel button is-small" @click="cancelEditSetting()" :disabled="isEditCancel() === false">
                キャンセル
              </button>
            </div>
            <!-- 媒体削除ボタン (編集中以外は非活性) -->
            <button class="setting-delete button is-small" @click="deleteMedia()" :disabled="isMediaDeletable() === false">
              媒体削除
            </button>
          </div>
          <!-- ToDo：別途実装するため非表示にしておく -->
          <!-- インポート/エクスポート -->
          <div class="data-in-out-area" v-show="false">
            <!-- インポートボタン -->
            <button class="data-import button is-small" @click="dataImport()" :disabled="isImportable() === false">
              インポート
            </button>
            <!-- エクスポートボタン -->
            <button class="data-export button is-small" @click="dataExport()" :disabled="isSelectedMedia() === false">
              エクスポート
            </button>
            <!-- テンプレートファイルダウンロード -->
            <button class="template-download button is-small" @click="templateDownload()">
              テンプレート
            </button>
          </div>
        </div>
      </div>
      <!-- 媒体情報 -->
      <div class="setting-body disp-scroll" ref="settingBody" :style="{height: settingBodyHeight}">
        <fieldset :disabled="isSettingEditable() === false">
          <!-- 設定テーブル -->
          <table class="setting-table">
            <!-- ヘッダ行 -->
            <thead>
              <tr>
                <th class="setting-col-item">
                  設定項目
                </th>
                <!-- 共通項目列 -->
                <th class="setting-col-value">
                  設定情報<br>
                </th>
              </tr>
            </thead>
            <!-- 設定 -->
            <tbody>
              <!-- 媒体ID -->
              <tr>
                <td class="setting-col-item">
                  媒体ID
                </td>
                <td class="setting-col-value">
                  {{getRecruitmentMediaId()}}
                </td>
              </tr>
              <!-- 媒体名 -->
              <tr>
                <td class="setting-col-item">
                  媒体名
                </td>
                <td class="setting-col-value">
                  <input class="table-cell" type="input" :value="getMediaName()" />
                </td>
              </tr>
              <!-- 短縮媒体名 -->
              <tr>
                <td class="setting-col-item">
                  媒体短縮名
                </td>
                <td class="setting-col-value">
                  <input class="table-cell" type="input" :value="getMediaShortName()" />
                </td>
              </tr>
              <!-- ログイン画面URL -->
              <tr>
                <td class="setting-col-item">
                  ログイン画面URL
                </td>
                <td class="setting-col-value">
                  <input class="table-cell" type="input" :value="getLoginUrl()" />
                </td>
              </tr>
              <!-- 応募元求人URL -->
              <tr>
                <td class="setting-col-item">
                  応募元求人URL<br>
                  (パラメーター挿入ヶ所に{}を入れる)
                </td>
                <td class="setting-col-value">
                  <input class="table-cell" type="input" :value="getRecruitmentUrl()" />
                </td>
              </tr>
              <!-- 応募元求人URL用パラメーター (複数ある場合はカンマ区切り) -->
              <tr>
                <td class="setting-col-item">
                  応募元求人URL用パラメーター<br>
                  (複数ある場合はカンマ区切り)
                </td>
                <td class="setting-col-value">
                  <input class="table-cell" type="input" :value="getRecruitmentUrlParameter()" />
                </td>
              </tr>
              <!-- 生年月日フォーマット ('%Y/%m/%d'や'%Y-%m-%d'、'%Y年%m月%d日'など) -->
              <tr>
                <td class="setting-col-item">
                  生年月日フォーマット<br>
                  ('%Y/%m/%d'や'%Y-%m-%d'、'%Y年%m月%d日'など)
                </td>
                <td class="setting-col-value">
                  <input class="table-cell" type="input" :value="getBirthDateFormat()" />
                </td>
              </tr>
              <!-- 応募日時フォーマット ('%Y/%m/%d %H:%M:%S'や'%Y/%m/%d'、'%Y-%m-%d'など) -->
              <tr>
                <td class="setting-col-item">
                  応募日時フォーマット<br>
                  ('%Y/%m/%d %H:%M:%S'や'%Y/%m/%d'、'%Y-%m-%d'など)
                </td>
                <td class="setting-col-value">
                  <input class="table-cell" type="input" :value="getSubscribeDateFormat()" />
                </td>
              </tr>
              <!-- 面接開始日時フォーマット('%Y/%m/%d %H:%M:%S'や'%Y/%m/%d'、'%Y-%m-%d'など) -->
              <tr>
                <td class="setting-col-item">
                  面接開始日時フォーマット<br>
                  ('%Y/%m/%d %H:%M:%S'や'%Y/%m/%d'、'%Y-%m-%d'など)
                </td>
                <td class="setting-col-value">
                  <input class="table-cell" type="input" :value="getInterviewDateFormat()" />
                </td>
              </tr>
              <!-- 媒体側応募者情報項目 -->
              <tr>
                <td class="setting-col-item">
                  媒体側応募者情報項目
                </td>
                <td class="setting-col-value">
                  <!-- 会社 -->
                  <div class="company">
                    <!-- 会社選択 -->
                    <select class="company-select" v-model="selectedItemCompanyIndex">
                      <option
                        v-for="(company, company_index) in getItemCompanyList()"
                        :key="company.company_name + '_' + company_index"
                        :value="company_index"
                      >
                        {{ company.company_name }}
                      </option>
                    </select>
                    <!-- 会社追加 -->
                    <button class="company-add button is-small" @click="addItemCompany()">
                      追加
                    </button>
                    <!-- 会社削除 -->
                    <button class="company-delete button is-small" @click="deleteItemCompany()">
                      削除
                    </button>
                  </div>
                  <div class="seeker-item">
                    <!-- 応募者情報項目 -->
                    <input class="table-cell" type="input" v-model="addInputSeekerItem" />
                    <button class="item-add button is-small" @click="addSeekerItem()">
                      追加
                    </button>
                    <button class="item-add button is-small" @click="updateSeekerItem()">
                      変更
                    </button>
                    <select class="item-select" size="10" v-model="selectedItemIndex">
                      <option
                        v-for="(item, item_index) in getItemList()"
                        :key="item.item_name + '_' + item_index"
                        :value="item_index"
                      >
                        {{ item.item_name }}
                      </option>
                    </select>
                    <!-- 応募者情報項目削除 -->
                    <button class="item-delete button is-small" @click="deleteSeekerItem()">
                      削除
                    </button>
                  </div>
                  <div class="seeker-item-history">
                    <!-- 項目名変更履歴 -->
                  </div>
                </td>
              </tr>
              <!-- 選考状況紐付け -->
              <tr>
                <td class="setting-col-item">
                  選考状況
                </td>
                <td class="setting-col-value">
                  <!-- 会社 -->
                  <div class="company">
                    <!-- 会社選択 -->
                    <select class="company-select" v-model="selectedProgressCompanyIndex">
                      <option
                        v-for="(company, company_index) in getProgressCompanyList()"
                        :key="company.company_name + '_' + company_index"
                        :value="company_index"
                      >
                        {{ company.company_name }}
                      </option>
                    </select>
                    <!-- 会社追加 -->
                    <button class="company-add button is-small" @click="addProgressCompany()">
                      追加
                    </button>
                    <!-- 会社削除 -->
                    <button class="company-delete button is-small" @click="deleteProgressCompany()">
                      削除
                    </button>
                  </div>
                  <!-- 選考状況紐付け -->
                  <div
                    class="progress-list disp-scroll"
                    v-for="(progressLink, progressLinkIndex) in getProgressLinkList()"
                    :key="progressLinkIndex"
                  >
                    <!-- 追加ボタン -->
                    <button class="button fluctuation-button" @click="addProgressLink(progressLinkIndex)">
                      <span class="mdi mdi-plus-box"></span>
                    </button>
                    <!-- 削除ボタン -->
                    <button class="button fluctuation-button" @click="deleteProgressLink(progressLinkIndex)">
                      <span class="mdi mdi-minus-box"></span>
                    </button>
                    <!-- 媒体側選考状況リスト -->
                    <select class="item-select" v-model="progressLink.media_progress_id">
                      <option
                        v-for="(mediaProgress, mediaProgressIndex) in getMediaProgressList()"
                        :key="mediaProgress.progress_id + '_' + mediaProgressIndex"
                        :value="mediaProgress.progress_id"
                      >
                        {{ mediaProgress.progress_name }}
                      </option>
                    </select>
                    <!-- 選考状況リスト -->
                    <select class="item-select" v-model="progressLink.progress_id">
                      <option
                        v-for="(progress, progressIndex) in getProgressList()"
                        :key="progress.progress_id + '_' + progressIndex"
                        :value="progress.progress_id"
                      >
                        {{ progress.progress_name }}
                      </option>
                    </select>
                  </div>
                </td>
              </tr>
              <!-- 媒体項目名とSeekerフィールド名を紐付け -->
              <tr>
                <td class="setting-col-item">
                  Seekerフィールド名紐付け
                </td>
                <td class="setting-col-value">
                  <!-- 会社 -->
                  <div class="company">
                    <!-- 会社選択 -->
                    <select class="company-select" v-model="selectedSeekerFieldCompanyIndex">
                      <option
                        v-for="(company, company_index) in getSeekerFieldCompanyList()"
                        :key="company.company_name + '_' + company_index"
                        :value="company_index"
                      >
                        {{ company.company_name }}
                      </option>
                    </select>
                    <!-- 会社追加 -->
                    <button class="company-add button is-small" @click="addSeekerFieldCompany()">
                      追加
                    </button>
                    <!-- 会社削除 -->
                    <button class="company-delete button is-small" @click="deleteSeekerFieldCompany()">
                      削除
                    </button>
                  </div>
                  <!-- 媒体項目名とSeekerフィールド名 -->
                  <div
                    class="seeker-field-item disp-scroll"
                    v-for="(SeekerFieldItem, SeekerFieldItemIndex) in getSeekerFieldItemList()"
                    :key="SeekerFieldItemIndex"
                  >
                    <!-- 追加ボタン -->
                    <button class="button fluctuation-button" @click="addItem(SeekerFieldItemIndex)">
                      <span class="mdi mdi-plus-box"></span>
                    </button>
                    <!-- 削除ボタン -->
                    <button class="button fluctuation-button" @click="deleteItem(SeekerFieldItemIndex)">
                      <span class="mdi mdi-minus-box"></span>
                    </button>
                    <!-- 媒体側項目リスト -->
                    <select class="item-select" v-model="SeekerFieldItem.seekerItem">
                      <option
                        v-for="(seekerItem, seekerItemIndex) in getSeekerItemList()"
                        :key="seekerItem.item_name + '_' + seekerItemIndex"
                        :value="seekerItem"
                      >
                        {{ seekerItem.item_name }}
                      </option>
                    </select>
                    <!-- Seekerフィールドリスト -->
                    <select class="item-select" v-model="SeekerFieldItem.seekerField">
                      <option
                        v-for="(seekerField, seekerFieldIndex) in getSeekerFieldList()"
                        :key="seekerField.field_name + '_' + seekerFieldIndex"
                        :value="seekerField"
                      >
                        {{ seekerField.field_name }}
                      </option>
                    </select>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </fieldset>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
// 処理中メッセージモーダル
import LoadingModal from '@/hrm-frontend/views/atomic/screen-components/purpose_components/common-components/LoadingModal'
// 会社選択モーダル
import CompanySelectModal from '@/hrm-frontend/views/atomic/screen-components/purpose_components/common-components/CompanySelectModal'
// アラートモーダル
import AlertModal from '@/hrm-frontend/views/atomic/screen-components/purpose_components/common-components/AlertModal'
// アラートモーダル用定義
import AlertDispFunctions from '@/hrm-frontend/modules/functions/alert-functions'
// フロントエンドログMixin
import FrontendLogMixin from '@/hrm-frontend/views/mixins/frontend-log-mixin'
// プロトタイプ宣言追加メソッド
import '@/hrm-frontend/modules/functions/prototype-extends'

export default {
  name: 'RecruitmentMediaInfoSetting',
  componentName: 'RecruitmentMediaInfoSetting',
  components: {
    // 処理中メッセージモーダル
    LoadingModal,
    // アラートモーダル
    AlertModal,
    // 会社選択モーダル
    CompanySelectModal,
  },
  mixins: [
    // フロントエンドログMixin
    FrontendLogMixin,
  ],
  data() {
    return {
      /**
       * ログタイトル
       * @type {String}
       */
      logTitle: '媒体情報設定',
      /**
       * 設定表の高さ
       * @type {String}
       */
      settingBodyHeight: 'calc(100vh - 185px)',
      /**
       * 変更前媒体情報
       * @type {Object}
       */
      changeBeforeData: {},
      /**
       * 媒体情報適用フラグ
       * @type {Boolean}
       */
      isAvailable: true,
      /**
       * 媒体新規作成フラグ
       * @type {Boolean}
       */
      isCreateSetting: false,
      /**
       * 媒体編集フラグ
       * @type {Boolean}
       */
      isEditSetting: false,
      /**
       * 選択中媒体
       * @type {Number}
       */
      selectedMediaIndex: 0,
      selectedItemCompanyIndex: 0,
      selectedProgressCompanyIndex: 0,
      selectedSeekerFieldCompanyIndex: 0,
      /**
       * 新規作成前選択中媒体
       * @type {Number}
       */
      beforeselectedMediaIndex: 0,
      /**
       * 会社選択フラグ
       * @type {Boolean}
       */
      isCompanySelect: false,
      isProgressAddCompany: false,
      isSeekerFieldAddCompany: false,
      isItemAddCompany: false,
      maxProgressLinkLength: 0,
      maxSeekerFieldLinkLength: 0,
      /**
       * レスポンス待ちフラグ
       * @type {Boolean}
       */
      isResponseWaiting: false,
      /**
       * 処理中タイトル
       * @type {String}
       */
      loadingTitle: '処理中です...',
      /**
       * 処理中メッセージ
       * @type {String}
       */
      loadingMessage: '',
    }
  },
  computed: {
    // 媒体連携関連ストア
    ...mapState('recruitmentMedia', [
      // 媒体情報設定リスト
      'recruitmentMediaInfoList',
      // 媒体情報テンプレート
      'recruitmentMediaInfoTemplate',
      // 選考状況リスト
      'progressList',
      // Seekerフィールドリスト
      'seekerFieldList',
    ]),
    // 会社情報関連ストア
    ...mapState('company', [
      // 会社リスト
      'companyList',
    ])
  },
  mounted () {
    // ウィンドウリサイズイベント登録
    window.addEventListener('resize', this.resizeMasterTable)
    try {
        // DOM描画後に高さ調整を行う
        this.$nextTick (() => {
          this.resizeMasterTable()
        })

        // 会社リスト取得
        this.$store.dispatch('company/getCompanyList')

        // 新規媒体情報テンプレート取得
        this.$store.dispatch('recruitmentMedia/getRecruitmentMediaInfoTemplate')

        // 媒体情報全設定取得
        this.loadingMessage = '媒体情報を取得しています。'
        // 処理中モーダルオープン
        this.isResponseWaiting = true
        // 選考状況リスト取得
        this.$store.dispatch('recruitmentMedia/getProgressList')
        // Seekerフィールドリスト種痘
        this.$store.dispatch('recruitmentMedia/getSeekerFieldList')
        // 全媒体情報取得
        this.$store.dispatch('recruitmentMedia/getAllRecruitmentMediaInfo')
          .catch((error) => {
            // エラーログ出力と登録
            console.error(error)
            this.sendSystemLog(
              this.LOG_LEVEL.ERROR,
              '媒体情報取得エラー',
              '媒体情報取得時に例外が発生しました。\n\n{0}'.format(error)
            )
            this.dispErrorAlert('媒体情報の取得リクエストに失敗しました。')
          })
          .finally(() => {
            // 処理中モーダルクローズ
            this.isResponseWaiting = false
          })
        /**
          HRモンスター内の選考状況一覧
          progress_list: [{progress_id: 選考状況ID, progress_name: '選考状況名', }, ...],

          Seekerフィールド一覧
          seeker_field_list: [{field_name_id: フィールド名ID, seeker_field_name: 'フィールド名'}],

          媒体情報は以下の形式で返してもらう
          recruitmentMediaInfoList: [
              {
                  media_id: '媒体ID',
                  media_name: '媒体名',
                  media_name_short: '媒体短縮名',
                  is_available: trueまたはfalse,
                  login_url: 'ログイン画面URL',
                  default_recruit_url: '応募元求人URL',
                  recruit_url_argument: '応募元求人URLパラメーター',
                  birth_date_format: '生年月日フォーマット',
                  subscribe_date_format: '応募日時フォーマット',
                  interview_date_format: '面接開始日時フォーマット',
                  媒体側応募者情報項目リスト
                  item_list: [{company_id: '会社ID', company_name: '会社名', item_list: [{item_name: '項目名', order: '表示順', history: {{ver: バージョン, item_name: 項目名}, ...}], ...],
                  媒体側選考状況リスト
                  media_progress_list:[{company_id: '会社ID', company_name: '会社名', progress_list: [{progress_name: '選考状況名', progress_id: '選考状況ID', order: '表示順'}, ...], ...],
                  選考状況紐付けリスト
                  progress_link_list:[{company_id: '会社ID', company_name: '会社名', progress_link_list: [{progress_id: 'HRモンスター内選考状況ID', media_progress_id: '媒体側選考状況ID', order: '表示順'}, ...], ...],
                  Seekerフィールド紐付けリスト
                  seeker_field_link_list: [{company_id: '会社ID', company_name: '会社名', seeker_field_link_list: [{item_id: '媒体側応募者情報項目ID', seeker_field_id: 'Seekerフィールド名ID', order: '表示順'}, ...], ...]
              }
          ]
        */
      } catch (error) {
        // エラーログ出力と登録
        console.error(error)
        this.sendSystemLog(
          this.LOG_LEVEL.ERROR,
          '媒体情報取得エラー',
          '媒体情報取得時に例外が発生しました。\n\n{0}'.format(error)
        )
        // 処理中モーダルクローズ
        this.isResponseWaiting = false
        this.dispErrorAlert('媒体情報の取得に失敗しました。')
      }
  },
  beforeDestroy () {
    // ウィンドウリサイズイベント破棄
    window.removeEventListener('resize', this.resizeMasterTable)
  },
  watch: {
    /**
     * 媒体情報リスト監視
     */
    recruitmentMediaInfoList () {
      try {
        if (this.recruitmentMediaInfoList.length > 0) {
          // 先頭のマスターIDを確認する
          if (!this.recruitmentMediaInfoList[0].media_id) {
            // nullまたはundefinedの場合は新規作成表示にする
            this.isCreateSetting = true
            // 空行を作成するために最大値を1にする
            this.maxProgressLinkLength = 1
            this.maxSeekerFieldLinkLength = 1
            // 各種会社選択を先頭にする
            this.selectedItemCompanyIndex = 0
            this.selectedProgressCompanyIndex = 0
            this.selectedSeekerFieldCompanyIndex = 0
          }
          // 媒体数より大きいインデックスの場合は先頭のマスターを選択する
          if (this.recruitmentMediaInfoList.length < this.selectedMediaIndex - 1) {
            this.selectedMediaIndex = 0
          }
          // 編集前データ退避
          this.changeBeforeData = JSON.parse(JSON.stringify(this.recruitmentMediaInfoList[this.selectedMediaIndex]))
        } else {
          // 1件も無くなった場合は、新規作成状態にする
          this.createMaster()
        }
      } catch (error) {
        console.error(error)
        // this.sendErrorLog(this.logTitle, error)
      }
    }
  },
  methods: {
    /**
     * リサイズ処理
     */
    resizeMasterTable () {
      try {
        if (this.$refs.settingHeader && this.$refs.settingBody) {
          // 要素が描画済みなら高さ調整を行う
          const settingHeaderHeight = this.$refs.settingHeader.clientHeight
          this.settingBodyHeight = this.$refs.settingBody.clientHeight
          if (settingHeaderHeight > 62) {
            // 媒体連携項目マスター操作部分の要素の高さが62pxを超えたら、表の高さを調整する
            this.settingBodyHeight = 'calc(100vh - ' + (185 + (settingHeaderHeight - 62)) + 'px)'
          }
        }
      } catch (error) {
        console.error(error)
        // this.sendErrorLog(this.logTitle, error)
      }
    },
    /**
     * 媒体情報編集中判定
     * 以下のいずれかの場合は媒体情報編集中
     * ・媒体情報編集
     * ・媒体新規作成
     */
    isSettingEditable () {
      return Boolean(this.isEditSetting === true || this.isCreateSetting === true)
    },
    /**
     * 媒体新規作成中判定
     */
    isMasterCreating () {
      return Boolean(this.isCreateSetting === true)
    },
    /**
     * 媒体削除ボタン有効判定
     * 媒体情報編集中で新規作成ではない場合に削除可能
     */
    isMediaDeletable () {
      return Boolean(this.isEditSetting === true && this.isCreateSetting === false)
    },
    /**
     * インポートボタン有効判定
     * 新規作成時のみインポート可能
     */
    isImportable () {
      return Boolean(this.isCreateSetting === true)
    },
    /**
     * エクスポートボタン有効判定
     * 編集中でない場合でマスター選択時のみエクスポート可能
     */
    isSelectedMedia () {
      return Boolean(this.isSettingEditable() === false && this.selectedMediaIndex >= 0)
    },
    /**
     * 初回新規作成判定
     */
    isFirstCreating () {
      return Boolean(this.isMasterCreating() === true && this.recruitmentMediaInfoList.length === 1)
    },
    /**
     * 編集キャンセル判定
     */
    isEditCancel () {
      // 初回新規作成時は変種キャンセル不可
      return Boolean(this.isFirstCreating() === false)
    },
    /**
     * 項目マスター名リスト取得
     */
    getMasterNameList () {
      try {
        let masterNameList = []
        if (this.mediaCooperationItemMasterList.length > 0) {
          // 項目マスター名のリストを作成する
          for (master of this.mediaCooperationItemMasterList) {
            masterNameList.push(master.master_name)
          }
        }
        return masterNameList
      } catch (error) {
        console.error(error)
        // this.sendErrorLog(this.logTitle, error)
      }
    },
    /**
     * 共通項目(カスタムカラム)リスト取得
     */
    getCommonItemList () {
      try {
        const mediaList = this.getMediaList(false)
        return mediaList.item_list
      } catch (error) {
        console.error(error)
        // this.sendErrorLog(this.logTitle, error)
      }
    },
    /**
     * 媒体リスト取得
     * @param {Number} isMedia 媒体フラグ
     */
    getMediaList (isMedia) {
      try {
        let media_list = []
        if (this.mediaCooperationItemMasterList.length > 0) {
          // 選択中マスターの媒体リストを取得する
          if (isMedia === true) {
            // 媒体のみ取得
            media_list = this.mediaCooperationItemMasterList[this.selectedMediaIndex].media_list
          } else {
            // 共通項目のみ取得
            media_list = this.mediaCooperationItemMasterList[this.selectedMediaIndex].common_list
          }
        }
        return media_list
      } catch (error) {
        console.error(error)
        // this.sendErrorLog(this.logTitle, error)
      }
    },
    /**
     * 共通項目データ取得
     * row_indexは1始まりなので注意
     * @param {Number} row_index 行数 (1始まり)
     */
    getCommonData (row_index) {
      try {
        let item_data = ''
        if (this.mediaCooperationItemMasterList.length > 0) {
          // 共通項目データの対象行のデータを取得する
          // 対象行に該当するデータがない場合はブランクを返す
          if (this.mediaCooperationItemMasterList[this.selectedMediaIndex].common_list.data_list.length > row_index - 1) {
            item_data = this.mediaCooperationItemMasterList[this.selectedMediaIndex].common_list.data_list[row_index - 1].item_data
          }
        }
        return item_data
      } catch (error) {
        console.error(error)
        // this.sendErrorLog(this.logTitle, error)
      }
    },
    /**
     * 媒体項目データ取得
     * row_indexは1始まりなので注意
     * @param {Number} media_index 媒体インデックス
     * @param {Number} row_index 行数 (1始まり)
     */
    getMediaData (media_index, row_index) {
      try {
        let item_data = ''
        if (this.mediaCooperationItemMasterList.length > 0) {
          // 媒体項目データの対象行のデータを取得する
          // 対象行に該当するデータがない場合はブランクを返す
          if (this.mediaCooperationItemMasterList[this.selectedMediaIndex].media_list[media_index].data_list.length > row_index - 1) {
            item_data = this.mediaCooperationItemMasterList[this.selectedMediaIndex].media_list[media_index].data_list[row_index - 1].item_data
          }
        }
        return item_data
      } catch (error) {
        console.error(error)
        // this.sendErrorLog(this.logTitle, error)
      }
    },
    /**
     * 媒体連携項目マスターテンプレートコピー
     * そのまま使うと参照ごとコピーしてしまうので、ディープコピーする
     */
    copyMediaCooperationItemMasterTemplateDataTemplate () {
      try {
        return JSON.parse(JSON.stringify(this.mediaCooperationItemMasterTemplate))
      } catch (error) {
        console.error(error)
        // this.sendErrorLog(this.logTitle, error)
      }
    },
    /**
     * 項目データテンプレートコピー
     * そのまま使うと参照ごとコピーしてしまうので、ディープコピーする
     */
    copyDataTemplate () {
      try {
        return JSON.parse(JSON.stringify(this.itemDataTemplate))
      } catch (error) {
        // this.sendErrorLog(this.logTitle, error)
      }
    },
    /**
     * 媒体連携項目マスター選択変更
     */
    changeMedia () {
      try {
        // 変更前データを退避
        this.changeBeforeData = JSON.parse(JSON.stringify(this.mediaCooperationItemMasterList[this.selectedMediaIndex]))
        // 最大項目数再取得
        this.cancelInit()
      } catch (error) {
        console.error(error)
        // this.sendErrorLog(this.logTitle, error)
      }
    },
    /**
     * 編集状態遷移
     */
    editInit () {
      try {
        // 選択中マスターのマスター名を入力ボックスに設定する
        this.masterName = this.mediaCooperationItemMasterList[this.selectedMediaIndex].master_name
      } catch (error) {
        console.error(error)
        // this.sendErrorLog(this.logTitle, error)
      }
    },
    /**
     * 編集状態初期化
     */
    cancelInit () {
      try {
        // 共通項目初期化
        this.applicantColumn = this.mediaCooperationItemMasterList[this.selectedMediaIndex].common_list.applicant_column
        // マスター名の入力ボックスを初期化する
        this.masterName = ''
        // マスター名編集状態初期化
        this.isEditName = false
        // マスター新規作成フラグ初期化
        this.isCreateSetting = false
        // マスター編集フラグ初期化
        this.isEditSetting = false
      } catch (error) {
        console.error(error)
        // this.sendErrorLog(this.logTitle, error)
      }
    },
    /**
     * 媒体連携項目マスター名編集遷移
     */
    editName () {
      try {
        // 編集状態に遷移
        this.editInit()
        // 設定名変更開始
        this.isEditName = true
      } catch (error) {
        console.error(error)
        // this.sendErrorLog(this.logTitle, error)
      }
    },
    /**
     * 媒体連携項目マスター名編集キャンセル
     */
    cancelEditName () {
      try {
        // マスター名変更キャンセル
        this.cancelInit()
      } catch (error) {
        console.error(error)
        // this.sendErrorLog(this.logTitle, error)
      }
    },
    /**
     * 媒体連携項目マスター適用操作
     */
    workingSwitch () {
      try {
        this.isAvailable = !this.isAvailable
      } catch (error) {
        console.error(error)
        // this.sendErrorLog(this.logTitle, error)
      }
    },
    /**
     * 媒体連携および自動採用フロー一時停止
     * @returns 処理結果
     */
    linkSuspend () {
      try {
        let result = true
        // 一時停止されていなければ一時停止処理を行う
        if (this.isSuspend === false) {
          result = confirm('実行する前に媒体連携および自動採用フローを一時停止します。\nよろしいですか？')
          if (result === true) {
            // 媒体連携および自動採用フロー一時停止API
            this.loadingMessage = '媒体連携および自動採用フローを停止しています。'
            this.isResponseWaiting = true
            this.$store.dispatch('background/batchTaskSuspend', this.$options._componentTag)
              .catch((error) => {
                console.error(error)
                // this.sendErrorLog(this.logTitle, error)
                alert('媒体連携および自動採用フローの停止に失敗しました。')
              })
              .finally(() => {
                // 処理中モーダルクローズ
                this.isResponseWaiting = false
              })
          }
        }
        return result
      } catch (error) {
        // 処理中モーダルクローズ
        this.isResponseWaiting = true
        console.error(error)
        // this.sendErrorLog(this.logTitle, error)
        alert('媒体連携および自動採用フローの停止に失敗しました。')
      }
    },
    /**
     * 媒体連携および自動採用フロー再開
     */
    linkResume () {
      try {
        // 一時停止されていれば一時停止解除処理を行う
        if (this.isSuspend === true) {
          // 媒体連携および自動採用フロー一時停止API
          this.loadingMessage = '媒体連携および自動採用フローを再開しています。'
          // 処理中モーダルオープン
          this.isResponseWaiting = true
          this.$store.dispatch('background/batchTaskResume', this.$options._componentTag)
            .catch((error) => {
              console.error(error)
              // this.sendErrorLog(this.logTitle, error)
              alert('媒体連携および自動採用フローの再開に失敗しました。')
            })
            .finally(() => {
              // 処理中モーダルクローズ
              this.isResponseWaiting = false
            })
        }
      } catch (error) {
        // 処理中モーダルクローズ
        this.isResponseWaiting = false
        console.error(error)
        // this.sendErrorLog(this.logTitle, error)
        alert('媒体連携および自動採用フローの再開に失敗しました。')
      }
    },
    /**
     * 媒体連携項目マスターデータ編集遷移
     */
    editSetting () {
      try {
        // 連携処理を一時停止する
        let result = this.linkSuspend()
        if (result === true) {
          // 編集状態に遷移
          this.editInit()

          // 設定編集開始
          this.isEditSetting = true
        }
      } catch (error) {
        console.error(error)
        // this.sendErrorLog(this.logTitle, error)
      }
    },
    /**
     * 媒体連携項目マスター編集キャンセル
     */
    cancelEditSetting () {
      try {
        // データを編集前に戻す
        if (this.isCreateSetting === true) {
          // 新規作成状態の場合は追加したテンプレートを削除する
          this.mediaCooperationItemMasterList.splice(this.selectedMediaIndex, 1)
          // 新規作成前のマスターを選択状態にする
          this.selectedMediaIndex = this.beforeselectedMediaIndex
        } else {
          // 編集状態の場合
          this.mediaCooperationItemMasterList[this.selectedMediaIndex] = JSON.parse(JSON.stringify(this.changeBeforeData))
        }

        // 自動連携再開
        this.linkResume()

        // 編集状態初期化
        this.cancelInit()
      } catch (error) {
        console.error(error)
        // this.sendErrorLog(this.logTitle, error)
      }
    },
    /**
     * 媒体連携項目マスター新規作成
     */
    createMaster () {
      try {
        // 連携処理を一時停止する
        let result = this.linkSuspend()
        if (result === true) {
          // 新規作成状態に遷移
          this.mediaCooperationItemMasterList.push(this.copyMediaCooperationItemMasterTemplateDataTemplate())
          this.beforeselectedMediaIndex = this.selectedMediaIndex
          this.selectedMediaIndex = this.mediaCooperationItemMasterList.length - 1

          // 編集状態に遷移
          this.editInit()
          // 設定編集開始
          this.isCreateSetting = true
          this.maxDataListLength = 1
        }
      } catch (error) {
        console.error(error)
        // this.sendErrorLog(this.logTitle, error)
      }
    },
    /**
     * 媒体連携項目マスター編集内容保存
     */
    saveMaster () {
      try {
        // 共通項目反映
        this.mediaCooperationItemMasterList[this.selectedMediaIndex].common_list.applicant_column = this.applicantColumn
        // マスター名反映
        this.mediaCooperationItemMasterList[this.selectedMediaIndex].master_name = this.masterName

        if (this.isCreateSetting === true || !this.mediaCooperationItemMasterList[this.selectedMediaIndex].master_id) {
          // 媒体連携項目マスター新規作成API
          this.loadingMessage = 'マスター設定を作成しています。'
          // 処理中モーダルオープン
          this.isResponseWaiting = true
          const recruitmentMediaIf = new recruitmentMediaInterface()
          const [result, createPayload] = recruitmentMediaIf.makeItemMasterCreatePayload(this.mediaCooperationItemMasterList[this.selectedMediaIndex])
          if (result === false) {
            alert(createPayload)
            return
          }
          if (createPayload) {
            this.$store.dispatch('recruitmentMedia/createMediaCooperationItemMaster', createPayload)
              .then(() => {
                // 自動連携再開
                this.linkResume()
                // 編集状態初期化
                this.cancelInit()
              })
              .catch((error) => {
                console.error(error)
                // this.sendErrorLog(this.logTitle, error)
                alert('マスター設定の新規作成に失敗しました。')
              })
              .finally(() => {
                // 処理中モーダルクローズ
                this.isResponseWaiting = false
              })
          } else {
            // 処理中モーダルクローズ
            this.isResponseWaiting = false
            alert('マスター設定の新規作成要求に失敗しました。')
          }
        } else {
          // 媒体連携項目マスター保存API
          this.loadingMessage = 'マスター設定を保存しています。'
          // 処理中モーダルオープン
          this.isResponseWaiting = true
          const recruitmentMediaIf = new recruitmentMediaInterface()
          const [result, savePayload] = recruitmentMediaIf.makeItemMasterSavePayload(this.mediaCooperationItemMasterList[this.selectedMediaIndex])
          if (result === false) {
            alert(savePayload)
            return
          }
          if (savePayload) {
            this.$store.dispatch('recruitmentMedia/saveMediaCooperationItemMaster', savePayload)
              .then(() => {
                // 自動連携再開
                this.linkResume()
                // 編集状態初期化
                this.cancelInit()
              })
              .catch((error) => {
                console.error(error)
                // this.sendErrorLog(this.logTitle, error)
                alert('マスター設定の保存に失敗しました。')
              })
              .finally(() => {
                // 処理中モーダルクローズ
                this.isResponseWaiting = false
              })
          } else {
            // 処理中モーダルクローズ
            this.isResponseWaiting = false
            alert('マスター設定の保存要求に失敗しました。')
          }
        }
      } catch (error) {
        // 処理中モーダルクローズ
        this.isResponseWaiting = false
        console.error(error)
        // this.sendErrorLog(this.logTitle, error)
        alert('マスター設定の保存に失敗しました。')
      }
    },
    /**
     * 選択中媒体連携項目マスター削除
     */
    deleteMedia () {
      try {
        const result = confirm('マスター設定を削除します。\nよろしいですか？')
        if (result === true) {
          // 連携処理を一時停止する
          let result = this.linkSuspend()
          if (result === true) {
            // 媒体連携項目マスターの削除
            this.loadingMessage = 'マスター設定を削除しています。'
            // 処理中モーダルオープン
            this.isResponseWaiting = true
            const recruitmentMediaIf = new recruitmentMediaInterface()
            const queryParameter = recruitmentMediaIf.makeItemMasterDataDeleteParameter(this.mediaCooperationItemMasterList[this.selectedMediaIndex].master_id)
            if (queryParameter) {
              const deleteData = {
                queryParameter: queryParameter,
                masterId: this.mediaCooperationItemMasterList[this.selectedMediaIndex].master_id
              }
              this.$store.dispatch('recruitmentMedia/deleteMediaCooperationItemMaster', deleteData)
                .then(() => {
                  // 自動連携再開
                  this.linkResume()
                  // 編集状態初期化
                  this.cancelInit()
                })
                .catch((error) => {
                  console.error(error)
                  // this.sendErrorLog(this.logTitle, error)
                  alert('マスター設定の削除に失敗しました。')
                })
                .finally(() => {
                  // 処理中モーダルクローズ
                  this.isResponseWaiting = false
                })
              } else {
                // 処理中モーダルクローズ
                this.isResponseWaiting = false
                alert('マスター設定の削除要求に失敗しました。')
              }
          }
        }
      } catch (error) {
        // 処理中モーダルクローズ
        this.isResponseWaiting = false
        console.error(error)
        // this.sendErrorLog(this.logTitle, error)
        alert('マスター設定の削除に失敗しました。')
      }
    },
    /**
     * インポート
     */
    dataImport () {
      try {
      } catch (error) {
        console.error(error)
        // this.sendErrorLog(this.logTitle, error)
      }
    },
    /**
     * エクスポート
     */
    dataExport () {
      try {
      } catch (error) {
        console.error(error)
        // this.sendErrorLog(this.logTitle, error)
      }
    },
    /**
     * インポートテンプレートダウンロード
     */
    templateDownload () {
      try {
      } catch (error) {
        console.error(error)
        // this.sendErrorLog(this.logTitle, error)
      }
    },
    /**
     * 媒体連携および自動採用フロー再開
     */
    restartBatch () {
      try {
        const result = confirm('媒体連携および自動採用フローを再開します。\nよろしいですか？')
        if (result === true) {
          // 媒体連携と自動採用フローを再開
          this.linkResume()
        }
      } catch (error) {
        console.error(error)
        // this.sendErrorLog(this.logTitle, error)
      }
    },
    /**
     * 会社選択
     * @param {Object} company 会社情報
     */
    selectedCompany (company) {
      this.isCompanySelect = false
      if (this.isItemAddCompany === true) {
        // 応募者情報項目カスタマイズ
        this.isItemAddCompany = false
        this.addItemCustomCompany(company)
      } else if (this.isProgressAddCompany === true) {
        // 選考状況紐付けカスタマイズ
        this.isProgressAddCompany = false
        this.addProgressCustomCompany(company)
      } else if (this.isSeekerFieldAddCompany === true) {
        // Seekerフィールド紐付けカスタマイズ
        this.isSeekerFieldAddCompany = false
        this.addSeekerFieldCustomCompany(company)
      }
    },
    addItemCompany () {
      // 応募者情報項目カスタマイズ状態にして会社選択モーダルを開く
      this.isItemAddCompany = true
      this.isCompanySelect = true
    },
    deleteItemCompany () {

    },
    addProgressCompany () {
      // 選考状況紐付けカスタマイズ状態にして会社選択モーダルを開く
      this.isProgressAddCompany = true
      this.isCompanySelect = true
    },
    deleteProgressCompany () {

    },
    addSeekerFieldCompany () {
      // Seekerフィールド紐付けカスタマイズ状態にして会社選択モーダルを開く
      this.isSeekerFieldAddCompany = true
      this.isCompanySelect = true
    },
    deleteSeekerFieldCompany () {

    },
    addItemCustomCompany (company) {
      // デフォルトの応募者情報項目リストをコピーして追加する
    },
    addProgressCustomCompany (company) {
      // デフォルトの選考状況リストをコピーして追加する
    },
    addSeekerFieldCustomCompany (company) {
      // デフォルトのSeekerフィールドリストをコピーして追加する
    },
    /**
     * 共通項目変更
     * @param {Object} event プルダウン変更イベント
     */
    changeCommonItem (event) {
      try {
        this.mediaCooperationItemMasterList[this.selectedMediaIndex].common_list.applicant_column = Number(event.target.value)
      } catch (error) {
        console.error(error)
        // this.sendErrorLog(this.logTitle, error)
      }
    },
    /**
     * 項目データ行追加
     * row_indexは1始まりなので注意
     * 「＋」ボタンを押下した次の行に追加するのでrow_indexのまま扱う
     * @param {Number} row_index 追加行番号
     */
    addItem (row_index) {
      try {
        // 共通項目データに行を追加
        this.mediaCooperationItemMasterList[this.selectedMediaIndex].common_list.data_list.splice(row_index, 0, this.copyDataTemplate())
        // 媒体項目データに行を追加
        for (let media of this.mediaCooperationItemMasterList[this.selectedMediaIndex].media_list) {
          media.data_list.splice(row_index, 0, this.copyDataTemplate())
        }
      } catch (error) {
        console.error(error)
        // this.sendErrorLog(this.logTitle, error)
      }
    },
    /**
     * 項目データ行削除
     * row_indexは1始まりなので注意
     * 「－」ボタンを押下した行を削除するので、row_indexをデクリメントする
     * @param {Number} row_index 削除行番号
     */
    deleteItem (row_index) {
      try {
        const delete_index = row_index - 1
        if (delete_index > 0) {
          // 共通項目データの行を削除
          this.mediaCooperationItemMasterList[this.selectedMediaIndex].common_list.data_list.splice(delete_index, 1)
          // 媒体項目データの行を削除
          for (let media of this.mediaCooperationItemMasterList[this.selectedMediaIndex].media_list) {
            media.data_list.splice(delete_index, 1)
          }
        } else {
          // 最後の1行を削除した場合は、空行と入れ替える
          // 共通項目データの行を入れ替える
          this.mediaCooperationItemMasterList[this.selectedMediaIndex].common_list.data_list.splice(delete_index, 1, this.copyDataTemplate())
          // 媒体項目データの行を入れ替える
          for (let media of this.mediaCooperationItemMasterList[this.selectedMediaIndex].media_list) {
            media.data_list.splice(delete_index, 1, this.copyDataTemplate())
          }
        }
      } catch (error) {
        console.error(error)
        // this.sendErrorLog(this.logTitle, error)
      }
    },
    /**
     * 共通項目データ変更
     * indexは1始まりなので注意
     * @param {Number} row_index 削除行番号
     * @param {Object} event テキストボックス変更イベント
     */
    changeCommonItemData (row_index, event) {
      try {
        this.mediaCooperationItemMasterList[this.selectedMediaIndex].common_list.data_list[row_index - 1].item_data = event.target.value
      } catch (error) {
        console.error(error)
        // this.sendErrorLog(this.logTitle, error)
      }
    },
    /**
     * 媒体項目データ変更
     * indexは1始まりなので注意
     * @param {Number} media_index 媒体列番号
     * @param {Number} row_index 削除行番号
     * @param {Object} event テキストボックス変更イベント
     */
    changeMediaItemData (media_index, row_index, event) {
      try {
        this.mediaCooperationItemMasterList[this.selectedMediaIndex].media_list[media_index].data_list[row_index - 1].item_data = event.target.value
      } catch (error) {
        console.error(error)
        // this.sendErrorLog(this.logTitle, error)
      }
    },
    /**
     * エラーアラート表示
     * @param {String} message エラーメッセージ
     */
     dispErrorAlert (message) {
      try {
        // アラートモーダル表示設定
        this.alertDispMethod = this.ALERT_CALL_METHOD_LIST.ERROR_ALERT
        this.alertData = AlertDispFunctions.createAlertData(
          'エラーが発生しました',
          message,
          AlertDispFunctions.ALERT_DEF.ICON_TYPE.DANGER,
          AlertDispFunctions.ALERT_DEF.DISP_TYPE.ALERT,
          this.$options._componentTag,
          this.ALERT_CALL_METHOD_LIST.ERROR_ALERT,
          null,
          null
        )
        // アラートモーダル表示
        this.isAlertOpen = true

      } catch (error) {
        // エラーログ出力と登録
        console.error(error)
        this.sendSystemLog(
          this.LOG_LEVEL.ERROR,
          'アラートモーダル表示エラー',
          'アラートモーダル表示時に例外が発生しました。\n表示内容：{0}\n\n{1}'.format(message, error)
        )
        // 確認モーダルクローズ
        this.isAlertOpen = false
      }
    },
    /**
     * アラートモーダルでのボタン押下結果
     * @param {Object} resultData ボタン押下結果
     */
    alertResult (resultData) {
      try {
        if (resultData[AlertDispFunctions.ALERT_DEF.RESULT_TEMPLATE_KEY_NAME.KEY_FUNCTION] === this.$options._componentTag) {
          if (resultData[AlertDispFunctions.ALERT_DEF.RESULT_TEMPLATE_KEY_NAME.KEY_WORD] === this.alertDispMethod) {
            // 呼び出したメソッドごとの処理
            if (this.alertDispMethod === this.ALERT_CALL_METHOD_LIST.LINK_RESUME) {
              // 自動連携再開
              if (resultData[AlertDispFunctions.ALERT_DEF.RESULT_TEMPLATE_KEY_NAME.VALUE] === AlertDispFunctions.ALERT_DEF.RESULT_TYPE.YES) {
                // 媒体連携と自動採用フローを再開
                this.linkResume()
              }
            } else if (this.alertDispMethod === this.ALERT_CALL_METHOD_LIST.LINK_SUSPEND) {
              // 自動連携一時停止
              if (resultData[AlertDispFunctions.ALERT_DEF.RESULT_TEMPLATE_KEY_NAME.VALUE] === AlertDispFunctions.ALERT_DEF.RESULT_TYPE.YES) {
                // 媒体連携および自動採用フロー一時停止API
                this.loadingMessage = '媒体連携および自動採用フローを停止しています。'
                this.isResponseWaiting = true
                this.$store.dispatch('background/batchTaskSuspend', this.$options._componentTag)
                  .catch((error) => {
                    // エラーログ出力と登録
                    console.error(error)
                    this.sendSystemLog(
                      this.LOG_LEVEL.ERROR,
                      '自動連携一時停止エラー',
                      '自動連携一時停止リクエスト時に例外が発生しました。\n処理メソッド：{0}\n\n{1}'
                        .format(this.alertDispMethod, error)
                    )
                    this.dispErrorAlert('媒体連携および自動採用フローの停止に失敗しました。')
                  })
                  .finally(() => {
                    // 後続処理
                    this.linkSuspendSubsequent(AlertDispFunctions.ALERT_DEF.RESULT_TYPE.YES)
                    // 処理中モーダルクローズ
                    this.isResponseWaiting = false
                  })
              } else {
                // 後続処理
                this.linkSuspendSubsequent(AlertDispFunctions.ALERT_DEF.RESULT_TYPE.NO)
              }
            } else if (this.alertDispMethod === this.ALERT_CALL_METHOD_LIST.DELETE_MASTER) {
              // 媒体連携項目マスター削除
              if (resultData[AlertDispFunctions.ALERT_DEF.RESULT_TEMPLATE_KEY_NAME.VALUE] === AlertDispFunctions.ALERT_DEF.RESULT_TYPE.YES) {
                // 連携処理を一時停止する
                this.linkSuspend(this.ALERT_CALL_METHOD_LIST.DELETE_MASTER)
              }
            }
          }
        }
      } catch (error) {
        // エラーログ出力と登録
        console.error(error)
        this.sendSystemLog(
          this.LOG_LEVEL.ERROR,
          'アラートモーダル結果エラー',
          'アラートモーダル結果処理時に例外が発生しました。\n処理メソッド：{0}\n処理結果：{1}\n\n{2}'
            .format(this.alertDispMethod, resultData, error)
        )
        // 処理中モーダルクローズ
        this.isResponseWaiting = false
        // 一時停止要求メソッド名初期化
        this.suspendMethodName = ''
      } finally {
        // アラートモーダルクローズ
        this.isAlertOpen = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.setting-component {
  width: 100%;
  // 上部操作パネル
  .setting-header {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    padding: 16px;
    // 媒体名選択
    .media-select-area {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      // 媒体名表示
      .name-disp {
        position: relative;
        display: flex;
        justify-content: flex-start;
        align-items: center;
        // 媒体選択プルダウン
        .setting-select {
          width: 200px;
          height: 30px;
        }
        // 有効設定
        .toggle-apply {
          width: 100px;
        }
      }
    }
    // 操作ボタン類
    .operation-button-area {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      // 媒体編集
      .setting-edit-area {
        padding-left: 32px;
        position: relative;
        display: flex;
        justify-content: flex-start;
        align-items: center;
        // 媒体編集ボタン
        .setting-edit {
          width: 100px;
        }
        // 媒体新規作成ボタン
        .setting-create {
          width: 100px;
        }
        // 媒体削除ボタン
        .setting-delete {
          width: 100px;
        }
        // 媒体編集キャンセルボタン
        .setting-edit-cancel {
          width: 100px;
        }
      }
      // インポート/エクスポート
      .data-in-out-area {
        position: relative;
        padding-left: 32px;
        // インポートボタン
        .data-import {
          width: 100px;
        }
        // エクスポートボタン
        .data-export {
          width: 100px;
        }
        // テンプレートボタン
        .template-download {
          width: 100px;
        }
      }
      // 閉じるボタン
      .setting-close-area {
        position: relative;
        padding-left: 128px;
        // 閉じるボタン
        .setting-close {
          width: 100px;
        }
      }
    }
  }
  // 共通項目データ設定
  .setting-body {
    display: flex;
    // 奇数行背景色
    @mixin row-color-odd {
      background-color:#ffeeee;
    }
    // 偶数行背景色
    @mixin row-color-even {
      background-color:#fcfcfc;
    }
    // セル枠
    @mixin cell-border {
      border: solid #000 1px;
    }
    // 項目設定テーブル
    .setting-table {
      display: table;
      table-layout: fixed;
      border-collapse:separate;
    }
    // 奇数データ行
    .setting-col-item {
      background-color:#ffeeee;
    }
    // 偶数データ行
    .setting-col-value {
      background-color:#fcfcfc;
    }
    // 共通項目選択プルダウン
    .common-item-select {
      width: 200px;
      height: 25px;
      margin: 4px;
    }
    // 項目データ増減ボタン
    .fluctuation-button {
      width: 20px;
      height: 27px;
      background: inherit;
      border: none;
      top: 0;
      bottom: 0;
      right: 0;
    }
    // 媒体項目選択プルダウン
    .media-item-select {
      width: 180px;
      height: 25px;
      margin: 4px;
    }
    // 各データ入力ボックス
    .table-cell {
      width: 100%;
      background: inherit;
      border: none;
      border-radius: 0;
    }
  }
}
</style>


// WEBPACK FOOTER //
// src/hrm-frontend/views/atomic/screen-components/operation-management/atomic-templates/RecruitmentMediaInfoSetting.vue